<template>
    <div class="form-container card shadow-sm">
      <div class="form-header d-flex justify-content-between align-items-center">
        <h4 class="form-title">{{ plano.id ? "Editar Plano de Matrícula" : "Novo Plano de Matrícula" }}</h4>
        <button class="btn btn-secondary btn-sm me-3" @click="voltar">Voltar</button>
      </div>
      <BaseForm :isFormValid="isFormValid" :isSubmitting="isSubmitting" @submit="salvarPlano" @cancel="voltar">
        <!-- Dados do Plano de Matrícula -->
        <FormSection title="Informações do Plano">
          <div class="d-flex justify-content-between">
            <!-- Categoria -->
            <div class="form-group flex-grow-1 me-3">
              <SelectField
                id="categoria"
                v-model="plano.categoria_id"
                label="Categoria <span class='required'>*</span>"
                :options="[
                  { value: '', text: 'Selecione uma categoria', disabled: true },
                  ...categorias.map(cat => ({ value: cat.value, text: cat.text }))
                ]"
                required
                :style="{ width: '250px' }"
                />
              <span v-if="errors.categoria_id" class="error-message">{{ errors.categoria_id }}</span>
            </div>

            <!-- Tipo de Plano -->
            <div class="form-group flex-grow-1 me-3">
              <SelectField
                id="tipo"
                v-model="plano.tipo_id"
                label="Tipo de Plano <span class='required'>*</span>"
                :options="[
                  { value: '', text: 'Selecione um tipo', disabled: true },
                  ...tipos.map(tipo => ({ value: tipo.value, text: tipo.text }))
                ]"
                required
                :style="{ width: '200px' }"
                />
              <span v-if="errors.tipo_id" class="error-message">{{ errors.tipo_id }}</span>
            </div>
  
            <!-- Status -->
            <div class="form-group">
              <label for="status">Status <span class="required">*</span></label>
              <div class="form-switch">
                <input
                  type="checkbox"
                  id="status"
                  v-model="plano.ativo"
                  class="form-check-input"
                />
                <label for="status" class="form-check-label">
                  {{ plano.ativo ? "Ativo" : "Inativo" }}
                </label>
              </div>
            </div>
          </div>
  
          <!-- Aulas por Semana, Valor da Parcela e Número de Parcelas -->
          <div class="d-flex justify-content-between">
            <div class="form-group flex-grow-1 me-3">
              <InputField
                id="aulas_semana"
                v-model="plano.aulas_semana"
                label="Aulas por Semana <span class='required'>*</span>"
                type="number"
                placeholder="Digite o número de aulas por semana"
                :class="{ 'error-input': errors.aulas_semana }"
                required
              />
              <span v-if="errors.aulas_semana" class="error-message">{{ errors.aulas_semana }}</span>
            </div>

            <div class="form-group flex-grow-1 me-3">
              <InputField
                id="valor_parcela"
                v-model="plano.valor_parcela"
                label="Valor da Parcela <span class='required'>*</span>"
                type="text"
                placeholder="Digite o valor da parcela"
                :class="{ 'error-input': errors.valor_parcela }"
                required
              />
              <span v-if="errors.valor_parcela" class="error-message">{{ errors.valor_parcela }}</span>
            </div>

            <div class="form-group flex-grow-1">
              <InputField
                id="quantidade_parcelas"
                v-model="plano.quantidade_parcelas"
                label="Número de Parcelas <span class='required'>*</span>"
                type="number"
                placeholder="Digite o número de parcelas"
                :class="{ 'error-input': errors.quantidade_parcelas }"
                required
              />
            </div>
          </div>
        </FormSection>
      </BaseForm>
    </div>
  </template>
  
  <script>
  import { reactive, ref, computed, onMounted } from "vue";
  import { useRouter, useRoute } from "vue-router";
  import axios from "@/services/axios";
  import BaseForm from "@/components/Layout/BaseForm.vue";
  import FormSection from "@/components/Layout/FormSection.vue";
  import SelectField from "@/components/Layout/SelectField.vue";
  import InputField from "@/components/Layout/InputField.vue";
  
  export default {
    components: { BaseForm, FormSection, SelectField, InputField },
    setup() {
      const router = useRouter();
      const route = useRoute();
  
      const plano = reactive({
        id: null,
        categoria_id: "",
        tipo_id: "",
        aulas_semana: null,
        valor_parcela: null,
        quantidade_parcelas: null,
        ativo: true,
      });
  
      const categorias = ref([]);
      const tipos = ref([]);
      const errors = reactive({});
      const isSubmitting = ref(false);
  
      const carregarCategorias = async () => {
        try {
          const { data } = await axios.get('/cadastros/categorias-de-planos', {
            params: { page: 1, limit: 10000 }
          });

          const lista = data?.data ?? data ?? [];

          const somenteAtivas = Array.isArray(lista)
            ? lista.filter(c => c.ativo !== false) // mantém se ativo=true ou undefined
            : [];

          // Ordenar por nome (ajuste a chave se necessário)
          somenteAtivas.sort((a, b) =>
            String(a.nome ?? '').localeCompare(String(b.nome ?? ''), 'pt-BR', {
              sensitivity: 'base',
              numeric: true
            })
          );

          categorias.value = somenteAtivas.map(cat => ({
            value: cat.id,
            text: cat.nome
          }));
        } catch (error) {
          console.error('Erro ao carregar categorias:', error);
          categorias.value = [];
        }
      };

        // dentro do setup()
/**
 * Carrega Tipos de Plano com opções de filtro e ordenação.
 * @param {Object} opts
 * @param {boolean} [opts.onlyActive=false]  - se true, envia ativo='true'
 * @param {'nome'|'duracao'|'id'} [opts.orderBy='nome'] - campo de ordenação (mapeado no backend)
 * @param {'asc'|'desc'} [opts.direction='asc'] - direção da ordenação
 */
const carregarTipos = async ({ onlyActive = false, orderBy = 'duracao', direction = 'asc' } = {}) => {
  try {
    // validação leve dos parâmetros
    const validOrderBy = ['nome', 'duracao', 'id'];
    if (!validOrderBy.includes(orderBy)) orderBy = 'nome';
    direction = (String(direction).toLowerCase() === 'desc') ? 'desc' : 'asc';

    // monta params para o backend (com limit=all já suportado aí)
    const params = {
      limit: 'all',
      sort: orderBy,
      order: direction,
    };
    if (onlyActive) params.ativo = 'true'; // backend espera string 'true'/'false'

    const { data } = await axios.get('/cadastros/tipos-planos', { params });

    // normaliza payload
    const rows = Array.isArray(data?.data) ? data.data : [];
    if (!Array.isArray(data?.data)) {
      console.warn('Formato inesperado de tipos-planos:', data);
    }

    // garante ordenação no front:
    if (orderBy === 'nome') {
      const collator = new Intl.Collator('pt-BR', { sensitivity: 'base', numeric: true });
      rows.sort((a, b) => collator.compare(a?.nome ?? '', b?.nome ?? ''));
      if (direction === 'desc') rows.reverse();
    } else if (orderBy === 'duracao') {
      rows.sort((a, b) => (a?.duracao ?? 0) - (b?.duracao ?? 0));
      if (direction === 'desc') rows.reverse();
    } else if (orderBy === 'id') {
      rows.sort((a, b) => (a?.id ?? 0) - (b?.id ?? 0));
      if (direction === 'desc') rows.reverse();
    }

    // remove duplicados por id (por segurança)
    const vistos = new Set();
    const unicos = rows.filter(r => {
      const id = r?.id;
      if (vistos.has(id)) return false;
      vistos.add(id);
      return true;
    });

    // shape esperado pelo SelectField: { value, text }
    tipos.value = unicos.map((tipo) => ({
      value: tipo.id,
      text: `${tipo.nome}${Number.isFinite(tipo?.duracao) ? ` (${tipo.duracao} dias)` : ''}`,
    }));
  } catch (error) {
    console.error('Erro ao carregar tipos de planos:', error?.response?.data || error);
    tipos.value = [];
  }
};
  
        const carregarPlano = async () => {
            const id = route.params.id;
            if (!id) return;

            try {
                const response = await axios.get(`/cadastros/planos-matriculas/${id}`);
                const data = response.data;

                plano.id = data.id;
                plano.categoria_id = data.categoria_id;
                plano.tipo_id = data.tipo_id;
                plano.aulas_semana = data.aulas_semana;
                plano.valor_parcela = formatarValorExibido(data.valor_parcela); // Formata para exibição
                plano.quantidade_parcelas = data.quantidade_parcelas;
                plano.ativo = data.ativo;
            } catch (error) {
                console.error("Erro ao carregar plano de matrícula:", error);
            }
        };
  
      const formatarValorParcela = () => {
            if (!plano.valor_parcela) return;

            // Substitui vírgulas por pontos e valida se é um número válido
            const valor = parseFloat(plano.valor_parcela.replace(',', '.'));

            if (!isNaN(valor)) {
                plano.valor_parcela = valor.toFixed(2); // Formata com duas casas decimais
            } else {
                errors.valor_parcela = "O valor da parcela deve ser um número válido.";
                plano.valor_parcela = ""; // Limpa o valor se for inválido
            }
        };

        const formatarValorExibido = (valor) => {
            return parseFloat(valor).toFixed(2).replace('.', ',');
        };

 
      const isFormValid = computed(() => {
        return(
          (plano.categoria_id !== null && plano.categoria_id.toString().trim() !== '') &&
          (plano.tipo_id !== null && plano.tipo_id.toString().trim() !== '') &&
          (plano.aulas_semana !== null && plano.aulas_semana.toString().trim() !== '') &&
          (plano.valor_parcela !== null && plano.valor_parcela.toString().trim() !== '') &&
          (plano.quantidade_parcelas !== null && plano.quantidade_parcelas.toString().trim() !== '') &&
          !Object.values(errors).some((error) => error)
        );
      });
  
      const salvarPlano = async () => {
        if (isSubmitting.value) return;
  
        // validateForm();
  
        if (!isFormValid.value) {
          console.error("O formulário contém erros:", errors);
          isSubmitting.value = false;
          return;
        }
  
        isSubmitting.value = true;
        try {
          const method = plano.id ? "put" : "post";
          const url = plano.id
            ? `/cadastros/planos-matriculas/${plano.id}`
            : "/cadastros/planos-matriculas";
  
          await axios[method](url, plano);
  
          router.push("/cadastros/planos-matriculas");
        } catch (error) {
          console.error("Erro ao salvar plano de matrícula:", error);
        } finally {
          isSubmitting.value = false;
        }
      };
  
      const voltar = () => {
        router.push("/cadastros/planos-matriculas");
      };
  
      onMounted(() => {
        carregarCategorias();
        carregarTipos();
        carregarPlano();
      });
  
      return {
        plano,
        categorias,
        tipos,
        errors,
        isFormValid,
        isSubmitting,
        salvarPlano,
        voltar,
        formatarValorParcela,
      };
    },
  };
  </script>
  
  <style scoped>
  .form-container {
    max-width: 90%;
    margin: 0 auto;
    padding: 20px;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #fff;
  }
  
  .form-title {
    margin-bottom: 10px;
    font-size: 1.5rem;
    color: #333;
  }
  
  .form-group {
    margin-bottom: 15px;
  }
  
  .required {
    color: red;
  }
  
  .form-switch {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  </style>
  